import { Node, ZapNodeData, DefaultNodeData, BuiltInApp } from "../types";
import { CanvasAdditionSchema } from "@zapier/service-providers";
import type { z } from "zod";

type CanvasAddition = z.infer<typeof CanvasAdditionSchema>;

/**
 * Returns the highest fidelity proposal data available from a CanvasAddition.
 * Priority order: zsl > outline > proposal
 */
function getHighestFidelityProposal(addition: CanvasAddition) {
  if (addition.zsl?.interfaces || addition.zsl?.tables || addition.zsl?.zaps) {
    return addition.zsl;
  }
  if (
    addition.outline?.interfaces ||
    addition.outline?.tables ||
    addition.outline?.zaps
  ) {
    return addition.outline;
  }
  return addition.proposal;
}

function getProjectIdFromAddition(
  addition: CanvasAddition,
): string | undefined {
  // Try outline first
  if (addition.outline?.interfaces?.[0]?.id) {
    return addition.outline.interfaces[0].id;
  }
  // Fall back to ZSL
  if (addition.zsl?.interfaces?.[0]?.id) {
    return addition.zsl.interfaces[0].id;
  }
  return undefined;
}

function getPageIdFromAddition(addition: CanvasAddition): string | undefined {
  // Try outline first
  if (addition.outline?.interfaces?.[0]?.pages?.[0]?.id) {
    return addition.outline.interfaces[0].pages[0].id;
  }
  // Fall back to ZSL
  if (addition.zsl?.interfaces?.[0]?.pages?.[0]?.id) {
    return addition.zsl.interfaces[0].pages[0].id;
  }
  return undefined;
}

function getTableIdFromAddition(addition: CanvasAddition): string | undefined {
  // Try outline first
  if (addition.outline?.tables?.[0]?.id) {
    return addition.outline.tables[0].id;
  }
  // Fall back to ZSL
  if (addition.zsl?.tables?.[0]?.id) {
    return addition.zsl.tables[0].id;
  }
  return undefined;
}

export function getAppServiceNodeData(
  appName: string,
  implementationId?: string,
) {
  if (!implementationId) return {};

  return {
    app: appName,
    services: [
      {
        currentImplementation: {
          id: implementationId,
        },
      },
    ],
  };
}

/**
 * Converts streamed canvas edit data into appropriate node data structures
 * based on the proposals
 */
export function parseAdditionCanvasEditsToNodeData(
  addition: CanvasAddition,
  {
    tablesApp,
    interfacesApp,
  }: {
    tablesApp: BuiltInApp;
    interfacesApp: BuiltInApp;
  },
): Partial<Node>[] {
  const { zsl } = addition;
  const proposalData = getHighestFidelityProposal(addition);
  const nodes: Partial<Node>[] = [];

  // Process interfaces
  //todo: jake - work on types here
  proposalData.interfaces?.forEach((interfaceData: any) => {
    const baseNodeData: DefaultNodeData = {
      // The value is on the title prop in prod, but on the name prop in staging
      label: interfaceData.title ?? interfaceData.name,
      ...getAppServiceNodeData(
        interfacesApp.name,
        interfacesApp.current_implementation_id,
      ),
      proposalId: addition.id,
      projectId: getProjectIdFromAddition(addition),
      pageId: getPageIdFromAddition(addition),
    };

    nodes.push({
      type: "default",
      data: baseNodeData,
    });
  });

  // Process tables
  //todo: jake - work on types here
  proposalData.tables?.forEach((tableData: any) => {
    const baseNodeData: DefaultNodeData = {
      // The value is on the title prop in prod, but on the name prop in staging
      label: tableData.title ?? tableData.name,
      ...getAppServiceNodeData(
        "Tables by Zapier",
        tablesApp.current_implementation_id,
      ),
      proposalId: addition.id,
      tableId: getTableIdFromAddition(addition),
    };

    nodes.push({
      type: "default",
      data: baseNodeData,
    });
  });

  // Process zaps
  //todo: jake - work on types here
  proposalData.zaps?.forEach((zapData: any, index: number) => {
    const baseNodeData: ZapNodeData = {
      label: zapData.title,
      proposalId: addition.id,
      zapId: zsl?.zaps?.[index]?.id ?? null,
      isExpanded: false,
    };

    nodes.push({
      type: "zap",
      data: baseNodeData,
    });
  });

  // TODO: Joanne to add support for agent nodes

  // If no nodes were created, return a default node
  if (nodes.length === 0) {
    return [
      {
        type: "default",
        data: {
          proposalId: addition.id,
        } as DefaultNodeData,
      },
    ];
  }

  return nodes;
}
